/**
 * The one place that talks to cloud-api over HTTP. Every function in
 * real-api.ts goes through `apiFetch`, never `fetch` directly, so the auth
 * header, the base URL and the error envelope parsing live in one spot.
 *
 * Mirrors the shape of `MockApiError` (lib/mock/latency.ts): `message` +
 * `code`: so a screen that already branches on `error.code` (BuildFailure,
 * the demo's failure states) keeps working unchanged whether it's reading a
 * mock or a real failure.
 */
import { clearSession, getToken, isImpersonating, stopImpersonation } from './auth/session';
import { captureApiError } from './analytics/posthog';

/**
 * The dashboard is also served on svcloud.dev, and there it talks to the same
 * cloud-api through api.svcloud.dev, so the whole product sits on one domain.
 * Keyed by the page's own hostname rather than a build variable because one
 * production build is served on both hostnames. Any other hostname (the
 * canonical one, staging, localhost) uses VITE_CLOUD_API_URL as before.
 */
const API_URL_BY_DASHBOARD_HOST: Record<string, string> = {
  'svcloud.dev': 'https://api.svcloud.dev',
};

export const CLOUD_API_URL =
  (typeof window !== 'undefined' ? API_URL_BY_DASHBOARD_HOST[window.location.hostname] : undefined) ??
  (import.meta.env.VITE_CLOUD_API_URL as string | undefined) ??
  'http://localhost:8787';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ErrorEnvelope {
  error: { code: string; message: string; details?: Record<string, unknown> };
  request_id: string;
}

export interface ApiFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Skip attaching the session bearer token: only the SSO exchange itself needs this. */
  unauthenticated?: boolean;
}

/**
 * GETs in flight, keyed by the token they were sent with and the path.
 *
 * One project page asks for the same thing from several places at once (the
 * shell resolves the project, the screen resolves it again, and listActivity,
 * listErrors and the overview each list deploys). Every duplicate costs more
 * than a round trip: Chrome holds concurrent requests for the same URL behind
 * one another on its HTTP cache entry, so four identical calls finish one
 * after another rather than together. Measured 2026-10-05 on staging: four
 * concurrent `obs/summary` calls landed at 4s, 8s, 12s and 16s.
 *
 * Only while the request is in flight, never after it settles: a read issued
 * after a write must see the write. Each caller gets its own copy of the
 * parsed body, so one screen mutating what it got cannot reach another.
 */
const inflight = new Map<string, Promise<unknown>>();

export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  if (method !== 'GET') return send<T>(path, options);

  const key = `${options.unauthenticated ? '' : (getToken() ?? '')} ${path}`;
  let pending = inflight.get(key) as Promise<T> | undefined;
  if (!pending) {
    pending = send<T>(path, options).finally(() => inflight.delete(key));
    inflight.set(key, pending);
  }
  const value = await pending;
  return value === undefined ? value : structuredClone(value);
}

async function send<T>(path: string, options: ApiFetchOptions): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (!options.unauthenticated) {
    const token = getToken();
    if (token) headers.authorization = `Bearer ${token}`;
  }

  const start = Date.now();
  let res: Response;
  try {
    res = await fetch(`${CLOUD_API_URL}${path}`, {
      method,
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });
  } catch (err) {
    const duration = Date.now() - start;
    const msg = err instanceof Error ? err.message : String(err);
    captureApiError({
      endpoint: path,
      method,
      status: 0,
      error_code: 'network_error',
      error_message: msg,
      duration_ms: duration,
      screen_path: typeof window !== 'undefined' ? window.location.pathname : '',
    });
    throw err;
  }

  const duration = Date.now() - start;

  if (res.status === 204) return undefined as T;

  if (!res.ok) {
    let envelope: ErrorEnvelope | undefined;
    try {
      envelope = (await res.json()) as ErrorEnvelope;
    } catch {
      // fall through with no envelope: a non-JSON error body (e.g. a 502 from in front of the Worker)
    }
    if (res.status === 401 && !options.unauthenticated) {
      if (isImpersonating()) {
        // A God View "view as" session expired (30 min TTL) or was stopped
        // from elsewhere: drop back to the admin's own session rather than
        // signing them out entirely. No router access here, so this is a
        // hard navigation rather than a client-side one.
        stopImpersonation();
        window.location.href = '/admin/god-view?expired=1';
      } else {
        // The session is gone (revoked, expired): there is no "signed in but
        // wrong" state this app models beyond bouncing back to sign-in.
        clearSession();
      }
    }

    const errorCode = envelope?.error.code ?? 'unknown_error';
    const errorMessage = envelope?.error.message ?? `Request failed (${res.status}).`;

    captureApiError({
      endpoint: path,
      method,
      status: res.status,
      error_code: errorCode,
      error_message: errorMessage,
      duration_ms: duration,
      screen_path: typeof window !== 'undefined' ? window.location.pathname : '',
    });

    throw new ApiError(
      errorMessage,
      errorCode,
      res.status,
    );
  }

  return (await res.json()) as T;
}
